import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card,
    alpha, Stack, Chip, Avatar, Paper, Fade, Grow,
} from '@mui/material';
import {
    School as SchoolIcon,
    MenuBook as BookIcon,
    EmojiEvents as TrophyIcon,
    Groups as GroupsIcon,
    LaptopMac as LmsIcon,
    CloudUpload as UploadIcon,
    Assignment as CourseIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    Dashboard as DashboardIcon,
    Science as ScienceIcon,
    Calculate as MathIcon,
    Biotech as BioIcon,
    Psychology as BrainIcon,
    Quiz as QuizIcon,
    BarChart as ChartIcon,
    TrendingUp as ProgressIcon,
    AccountTree as StructureIcon,
} from '@mui/icons-material';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1d748b 0%, #52d370 100%)';
const JEE_COLOR = '#6f42c1';
const NEET_COLOR = '#198754';

// ==================== IMPULZ NEET / JEE DATA ====================
const jeeNeetData = {
    name: 'Impulz NEET / JEE',
    shortName: 'IN',
    tagline: 'Your Path to Competitive Excellence',
    color: JEE_COLOR,
    type: 'Competitive Examination Learning Platform',
    about: 'Impulz NEET / JEE is a dedicated digital education platform designed to support students preparing for competitive entrance examinations such as JEE Main, JEE Advanced and NEET.',
    lmsIntro: 'We developed and implemented a centralized platform to manage student registration, examination programmes, subjects, syllabus, chapters, topics, learning materials, question banks, online tests, examinations, results and student progress.',
    lmsFeatures: [
        {
            title: 'Student Registration',
            desc: 'Allows students to register and create their personalized learning accounts.',
            icon: <PersonIcon />,
        },
        {
            title: 'Exam Management',
            desc: 'Manages different competitive examinations such as JEE Main, JEE Advanced and NEET.',
            icon: <SchoolIcon />,
        },
        {
            title: 'Subject Management',
            desc: 'Organizes subjects according to the selected examination.',
            icon: <CourseIcon />,
        },
        {
            title: 'Syllabus Management',
            desc: 'Provides structured examination-wise syllabus information.',
            icon: <BookIcon />,
        },
        {
            title: 'Chapter Management',
            desc: 'Divides subjects into individual chapters for systematic learning.',
            icon: <StructureIcon />,
        },
        {
            title: 'Topic Management',
            desc: 'Organizes chapters into detailed topics and sub-topics.',
            icon: <DashboardIcon />,
        },
        {
            title: 'Learning Materials',
            desc: 'Provides students with notes, study materials, PDFs and other educational resources.',
            icon: <UploadIcon />,
        },
        {
            title: 'Question Bank',
            desc: 'Maintains examination-oriented questions categorized by subject, chapter, topic and difficulty level.',
            icon: <QuizIcon />,
        },
        {
            title: 'Online Practice Tests',
            desc: 'Enables students to practice questions through topic-wise and subject-wise tests.',
            icon: <LmsIcon />,
        },
        {
            title: 'Performance Analysis',
            desc: 'Helps students understand their strengths, weaknesses and overall performance.',
            icon: <ChartIcon />,
        },
        {
            title: 'Progress Tracking',
            desc: 'Tracks student learning progress across subjects, chapters and topics.',
            icon: <ProgressIcon />,
        },
    ],
    examAreas: [
        {
            exam: 'JEE',
            color: JEE_COLOR,
            icon: <MathIcon />,
            subjects: [
                { name: 'JEE Main', icon: <TrophyIcon /> },
                { name: 'JEE Advanced', icon: <TrophyIcon /> },
                { name: 'Physics', icon: <ScienceIcon /> },
                { name: 'Chemistry', icon: <ScienceIcon /> },
                { name: 'Mathematics', icon: <MathIcon /> },
            ],
        },
        {
            exam: 'NEET',
            color: NEET_COLOR,
            icon: <BioIcon />,
            subjects: [
                { name: 'NEET', icon: <TrophyIcon /> },
                { name: 'Physics', icon: <ScienceIcon /> },
                { name: 'Chemistry', icon: <ScienceIcon /> },
                { name: 'Biology', icon: <BioIcon /> },
                { name: 'Botany', icon: <BioIcon /> },
                { name: 'Zoology', icon: <BioIcon /> },
            ],
        },
    ],
    academicStructure: [
        'Exam',
        'Subject',
        'Syllabus',
        'Chapter',
        'Topic',
        'Learning Material',
        'Question Bank',
        'Practice Test',
        'Mock Exam',
        'Result',
        'Performance Analysis',
    ],
    quickInfo: [
        // { label: 'Client', value: 'Impulz NEET / JEE', icon: <SchoolIcon /> },
        { label: 'Target', value: 'JEE & NEET Aspirants', icon: <GroupsIcon /> },
        { label: 'Solution', value: 'Competitive Exam', icon: <LmsIcon /> },
        { label: 'Platform', value: 'Impulz Learning Platform', icon: <BrainIcon /> },
    ],
};

const ImpulzNEETJEEPage = () => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!mounted) return null;

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            <Navbar />

            {/* ==================== HERO ==================== */}
            <Box
                sx={{
                    background: BRAND_GRADIENT,
                    pt: { xs: 8, md: 10 },
                    pb: { xs: 12, md: 16 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: '5%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1,mt:2 }}>
                    <Fade in timeout={800}>
                        <Box>
                            <Chip
                                label="COMPETITIVE EXAM"
                                sx={{
                                    mb: 3,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                }}
                            />

                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '2.4rem', md: '3.8rem' },
                                    fontWeight: 800,
                                    mb: 2,
                                    color: 'white',
                                    lineHeight: 1.2,
                                }}
                            >
                                {jeeNeetData.name}
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.95)',
                                    maxWidth: 850,
                                    fontWeight: 400,
                                    fontSize: { xs: '1.05rem', md: '1.3rem' },
                                    mb: 3,
                                }}
                            >
                                {jeeNeetData.type}
                            </Typography>

                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <Chip
                                    icon={<MathIcon sx={{ color: 'white !important' }} />}
                                    label="JEE Main & Advanced"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.95rem' }}
                                />
                                <Chip
                                    icon={<BioIcon sx={{ color: 'white !important' }} />}
                                    label="NEET"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.95rem' }}
                                />
                            </Stack>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* ==================== OVERVIEW CARD ==================== */}
            <Container maxWidth="xl" sx={{ mt: -10, mb: 5, position: 'relative', zIndex: 2 }}>
                <Grow in timeout={1000}>
                    <Paper elevation={24} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: 'white' }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={8}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                    {/* <Avatar
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            background: JEE_COLOR,
                                            fontWeight: 800,
                                            fontSize: '1.5rem',
                                        }}
                                    >
                                        {jeeNeetData.shortName}
                                    </Avatar> */}
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                            {jeeNeetData.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                            {jeeNeetData.type}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.7 }}>
                                    {jeeNeetData.about}
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.7 }}>
                                    {jeeNeetData.lmsIntro}
                                </Typography>
                            </Grid>

                            {/* Quick Info */}
                            <Grid item xs={12} md={4}>
                                <Stack spacing={2}>
                                    {jeeNeetData.quickInfo.map((info) => (
                                        <Paper
                                            key={info.label}
                                            sx={{ p: 2, bgcolor: alpha(JEE_COLOR, 0.08), borderRadius: 2 }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{ color: JEE_COLOR }}>{info.icon}</Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                        {info.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                                        {info.value}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grow>
            </Container>

            {/* ==================== KEY EXAMINATION AREAS ==================== */}
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <TrophyIcon sx={{ color: JEE_COLOR, fontSize: 36 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        Key Examination Areas
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem' }}>
                    Examinations and subjects covered by the platform
                </Typography>

                <Grid container spacing={3}>
                    {jeeNeetData.examAreas.map((area, i) => (
                        <Grid item xs={12} md={6} key={area.exam}>
                            <Grow in timeout={(i + 1) * 150}>
                                <Card
                                    sx={{
                                        p: 4,
                                        borderRadius: 3,
                                        height: '100%',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                        borderTop: `4px solid ${area.color}`,
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: `0 20px 40px ${alpha(area.color, 0.25)}`,
                                        },
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: 2,
                                                bgcolor: alpha(area.color, 0.12),
                                                color: area.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 32,
                                            }}
                                        >
                                            {area.icon}
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: area.color }}>
                                            {area.exam}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {area.subjects.map((subject) => (
                                            <Chip
                                                key={subject.name}
                                                icon={subject.icon}
                                                label={subject.name}
                                                sx={{
                                                    bgcolor: alpha(area.color, 0.1),
                                                    color: area.color,
                                                    fontWeight: 600,
                                                    fontSize: '0.95rem',
                                                    mb: 1,
                                                    '& .MuiChip-icon': { color: area.color },
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Card>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ==================== LMS SOLUTIONS / FEATURES ==================== */}
            <Box sx={{ bgcolor: '#f8f9fa', py: 6 }}>
                <Container maxWidth="xl">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <LmsIcon sx={{ color: JEE_COLOR, fontSize: 36 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            LMS Solutions
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem' }}>
                        Key modules we developed for the Impulz NEET / JEE platform
                    </Typography>

                    <Grid container spacing={3}>
                        {jeeNeetData.lmsFeatures.map((feature, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={feature.title}>
                                <Grow in timeout={(i + 1) * 80}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                            transition: 'all 0.25s',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: `0 20px 40px ${alpha(JEE_COLOR, 0.2)}`,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: 2,
                                                bgcolor: alpha(JEE_COLOR, 0.12),
                                                color: JEE_COLOR,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '1.1rem' }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                                            {feature.desc}
                                        </Typography>
                                    </Card>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==================== ACADEMIC STRUCTURE ==================== */}
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <SecurityIcon sx={{ color: JEE_COLOR, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Academic Structure
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem', lineHeight: 1.7 }}>
                        This structured approach enables students to follow their preparation systematically from syllabus understanding through practice, assessment and performance tracking.
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        {jeeNeetData.academicStructure.map((step, i) => (
                            <React.Fragment key={step}>
                                <Chip
                                    label={step}
                                    sx={{
                                        bgcolor: alpha(JEE_COLOR, 0.12),
                                        color: JEE_COLOR,
                                        fontWeight: 700,
                                        fontSize: '0.95rem',
                                        mb: 1,
                                        height: 36,
                                        px: 1,
                                    }}
                                />
                                {i < jeeNeetData.academicStructure.length - 1 && (
                                    <Typography sx={{ color: alpha(JEE_COLOR, 0.5), fontWeight: 800, mb: 1 }}>
                                        →
                                    </Typography>
                                )}
                            </React.Fragment>
                        ))}
                    </Stack>
                </Paper>
            </Container>

            <HomeFooter />
        </Box>
    );
};

export default ImpulzNEETJEEPage;